import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { Tooltip } from "@blueprintjs/core";
import { PopoverPosition } from "@blueprintjs/core";
import CalculationStatusIcon from "../../../CalculationStatusIcon";
import TextTruncate from "components/TextTruncate";
import RadioInputGroup, { RadioInputViewType } from "components/Controls/RadioInputGroup";
import SourceName from "../../../SourceName";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { getSimulationTimestampOptions, getSourceNameTooltip, getSourceNameTooltipLabel, getTimingSummaryText, prepareSpaceviewerSource, } from "store/rs-workspace/utils/results.utils";
import { ResultElementSourceType, ResultElementSourceAppearance, CalculationStatusSource, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { persistedInsightsDataSelector, recipeCustomVariablesSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { calculationSpaceviewerResultsSelector, calculationSpaceviewerSourceStatusSelector, } from "store/rs-workspace/selectors/results.selectors";
import { INPUT } from "shared/labels";
import { compareResultItemIds } from "utils/data";
import ErrorIcon from "icons/info_triangle.svg";
import * as styles from "../../styles.module.scss";
const SourceRowPreview = ({ source, resultSetId, elementId, parameters, handleChangeView }) => {
    const parameterTypes = useSelector(parameterTypesSelector);
    const variableOptionsList = useSelector(processVariablesOptionsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsights = useSelector(persistedInsightsDataSelector);
    const processVariables = useSelector(processVariablesSelector);
    const { accuracy, isCompleted, isFilled, calculationStatus, resultStatus, fetching, resultsAreAvailable, hasNoParameters, error, sourceTypeLabel, } = useSelector(calculationSpaceviewerSourceStatusSelector(resultSetId, elementId, source, parameters));
    const spaceViewerResults = useSelector(calculationSpaceviewerResultsSelector);
    const sourceResult = useMemo(() => spaceViewerResults
        ?.find((item) => compareResultItemIds(item, { resultSetElementId: elementId, resultSetId }))
        ?.sources.find(({ sourceId }) => sourceId === source.id), [elementId, resultSetId, source.id, spaceViewerResults]);
    const recipeId = useMemo(() => {
        const isSimulation = source?.source?.type === ResultElementSourceType.Simulation;
        if (!isSimulation) {
            return undefined;
        }
        return simulations.find(({ id }) => id === source?.source?.id)
            ?.recipeId;
    }, [source, simulations]);
    const customVariables = useSelector(recipeCustomVariablesSelector([recipeId]));
    const preparedSource = useMemo(() => prepareSpaceviewerSource(source, variableOptionsList, parameterTypes, simulations, insights, persistedInsights, processVariables, recipes), [
        insights,
        parameterTypes,
        persistedInsights,
        processVariables,
        simulations,
        source,
        variableOptionsList,
        recipes,
    ]);
    const timingSummaryText = useMemo(() => {
        if (preparedSource?.type !== ResultElementSourceType.Simulation) {
            return "";
        }
        const timestampOptions = getSimulationTimestampOptions(preparedSource.simulationId, simulations, recipes);
        return getTimingSummaryText(preparedSource.timing || null, timestampOptions, processVariables, customVariables);
    }, [
        preparedSource?.type,
        preparedSource.simulationId,
        preparedSource.timing,
        simulations,
        recipes,
        processVariables,
        customVariables,
    ]);
    const appearance = useMemo(() => {
        if (!isCompleted) {
            return ResultElementSourceAppearance.Incomplete;
        }
        if (calculationStatus === CalculationStatusSource.InProgress || fetching) {
            return ResultElementSourceAppearance.InProgress;
        }
        if (calculationStatus === CalculationStatusSource.Paused) {
            return ResultElementSourceAppearance.Paused;
        }
        if (error && resultStatus === SimulationRunState.ERROR) {
            return ResultElementSourceAppearance.Error;
        }
        if (resultsAreAvailable || resultStatus === SimulationRunState.FINISHED) {
            return ResultElementSourceAppearance.Calculated;
        }
        return ResultElementSourceAppearance.Ready;
    }, [isCompleted, calculationStatus, fetching, error, resultStatus, resultsAreAvailable]);
    const tooltip = useMemo(() => {
        const tooltipLabel = getSourceNameTooltipLabel(sourceTypeLabel, source.source, appearance, simulations, cellModels, recipes);
        return getSourceNameTooltip(appearance, tooltipLabel, error, hasNoParameters, isFilled);
    }, [
        appearance,
        error,
        hasNoParameters,
        isFilled,
        sourceTypeLabel,
        source.source,
        simulations,
        cellModels,
        recipes,
    ]);
    const isSourceDataBiased = useMemo(() => sourceResult?.points?.some(({ errorCount }) => errorCount > 0), [sourceResult?.points]);
    return (React.createElement("div", { className: styles.row },
        React.createElement("div", { className: classNames(styles.cell, styles.view) },
            React.createElement(RadioInputGroup, { value: preparedSource.view ? `${preparedSource.id}` : "", items: [{ value: `${preparedSource.id}`, label: "" }], onChange: handleChangeView, viewType: RadioInputViewType.RADIO, className: styles.radio })),
        React.createElement(SourceName, { className: classNames(styles.cell, styles.source), tooltip: tooltip },
            React.createElement("div", { className: styles["source-container"] },
                React.createElement(CalculationStatusIcon, { accuracy: accuracy, appearance: appearance, className: styles["status-icon"] }),
                React.createElement(TextTruncate, { text: preparedSource.source?.label || INPUT.UNDEFINED, className: classNames(styles["preview-value"], {
                        [styles.accented]: appearance === ResultElementSourceAppearance.Incomplete ||
                            appearance === ResultElementSourceAppearance.Error,
                    }) }))),
        React.createElement("div", { className: classNames(styles.cell, styles.variable) }, preparedSource.type === ResultElementSourceType.Simulation && (React.createElement(TextTruncate, { text: preparedSource._variableName?.label || INPUT.UNDEFINED, className: styles["preview-value"] }))),
        React.createElement("div", { className: classNames(styles.cell, styles.unit) }, preparedSource.type === ResultElementSourceType.Simulation && (React.createElement(TextTruncate, { text: preparedSource.units?.label || INPUT.UNDEFINED, className: styles["preview-value"] }))),
        React.createElement("div", { className: classNames(styles.cell, styles.timing, { [styles.filled]: !!timingSummaryText }) },
            preparedSource.type === ResultElementSourceType.Simulation && (React.createElement(React.Fragment, null,
                React.createElement(TextTruncate, { text: timingSummaryText || INPUT.UNDEFINED }))),
            isSourceDataBiased && (React.createElement(Tooltip, { minimal: true, content: React.createElement("p", { className: styles["tooltip-title"] }, "Data is biased"), portalClassName: styles.tooltipPortal, popoverClassName: styles.tooltip, position: PopoverPosition.BOTTOM, className: styles.biasedTooltip, rootBoundary: "viewport", boundary: "clippingParents", openOnTargetFocus: false },
                React.createElement(ErrorIcon, { className: styles["error-icon"] })))),
        React.createElement("div", { className: classNames(styles.cell, styles.delete) })));
};
export default SourceRowPreview;
